import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * `{ data, loading, error, reload }` — the whole data-fetching story for a screen.
 *
 * NO DATA-FETCHING LIBRARY IS ADDED (roadmap §3.1). React Query and friends earn their
 * weight on apps with shared, long-lived, revalidating caches. Here every list is
 * server-paged and server-filtered, so its data is already scoped to one screen and one
 * filter state — a cache would mostly be re-fetching on mount, which is what this does
 * in thirty lines and no new dependency (R5).
 *
 *   const { data, loading, error, reload } = useResource(
 *     () => listUsers({ page, search }),
 *     [page, search],
 *   )
 *
 * Two things it does that a bare `useEffect` gets wrong:
 *
 * - A superseded response never lands. Type into a search box and requests finish out
 *   of order; without the guard the screen settles on whichever was slowest.
 * - `loading` is true from the first render, not after the first effect. Otherwise the
 *   screen paints its EMPTY state for one frame before the request has even started.
 */
export function useResource(loader, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null })
  const [nonce, setNonce] = useState(0)

  /* Held in a ref so changing the loader — an inline arrow, so every render — does not
     re-run the effect. `deps` is what decides that, exactly as the caller wrote it. */
  const loaderRef = useRef(loader)
  loaderRef.current = loader

  useEffect(() => {
    let current = true
    const controller = new AbortController()

    setState((previous) => ({ ...previous, loading: true, error: null }))

    loaderRef
      .current(controller.signal)
      .then((data) => {
        if (current) setState({ data, loading: false, error: null })
      })
      .catch((error) => {
        if (!current || error?.name === 'AbortError') return
        setState({ data: null, loading: false, error })
      })

    return () => {
      current = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { ...state, reload }
}

export default useResource
